import { FC, useMemo } from 'react';
import Link from 'next/link';
import styled from 'styled-components';

import { Tabs } from '@/features/vfo/uikit/Tabs';
import { ApiCaseResponseV2 } from '@/shared/api';
import { COLORS } from '@/shared/constants/colors';
import { useTranslation } from '@/shared/utils/i18n/useTranslation';

import { CaseItem } from '../CaseItem';

import { EmptyCasesMessage } from './EmpyCasesMessage';

type CasesListProps = {
  cases: ApiCaseResponseV2[];
  isDashboard?: boolean;
};

export const CasesList: FC<CasesListProps> = ({ cases, isDashboard }) => {
  const { t } = useTranslation('features/vfo');

  const casesData = useMemo(() => {
    const groupCases = cases.filter((item) => !item.isPrivateCase);
    const privateCases = cases.filter((item) => item.isPrivateCase);
    return {
      groupCases,
      privateCases,
    };
  }, [cases]);

  return (
    <Container>
      {isDashboard && (
        <TitleWrapper>
          <span>
            <b>{t('casesList.title')}</b> ({cases.length})
          </span>
          {cases.length > 0 && (
            <Link
              style={{ color: COLORS.systemSky200, fontWeight: 700, fontSize: 14 }}
              href="/vfo/cases"
            >
              {t('casesList.viewAll')}
            </Link>
          )}
        </TitleWrapper>
      )}
      {!cases.length ? (
        <EmptyCasesMessage text={t('casesList.empty')} />
      ) : isDashboard ? (
        <List>
          {cases.slice(0, 8).map((item) => (
            <CaseItem data={item} key={item.id} />
          ))}
        </List>
      ) : (
        <Tabs
          items={[
            {
              key: 'groupCases',
              label: (
                <>
                  <TabLabel>{t('casesList.groupCases')}</TabLabel>
                  {casesData.groupCases.length > 0 && (
                    <Counter>{casesData.groupCases.length}</Counter>
                  )}
                </>
              ),
              children: (
                <List>
                  {casesData.groupCases.map((item) => (
                    <CaseItem data={item} key={item.id} />
                  ))}
                </List>
              ),
            },
            {
              key: 'privateCases',
              label: (
                <>
                  <TabLabel>{t('casesList.privateCases')}</TabLabel>
                  {casesData.privateCases.length > 0 && (
                    <Counter>{casesData.privateCases.length}</Counter>
                  )}
                </>
              ),
              children:
                casesData.privateCases.length === 0 ? (
                  <EmptyCasesMessage text={t('casesList.noPrivate')} />
                ) : (
                  <List>
                    {casesData.privateCases.map((item) => (
                      <CaseItem data={item} key={item.id} />
                    ))}
                  </List>
                ),
            },
          ]}
        ></Tabs>
      )}
    </Container>
  );
};

const Container = styled.div`
  padding: 24px;
  background: ${COLORS.white};
  border-radius: 24px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  container-type: inline-size;
`;

const TitleWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 16px;
`;

const List = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;

  @container (width > 990px) {
    grid-template-columns: repeat(4, 1fr);
  }

  @container (width <= 530px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @container (width <= 420px) {
    grid-template-columns: repeat(1, 1fr);
  }
`;

const Counter = styled.div`
  padding: 2px 8px;
  background: ${COLORS.systemDark50A};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${COLORS.systemDark300};
  border-radius: 6px;
  width: 23px;
  height: 20px;
  font-size: 12px;
  font-weight: 400;
`;

const TabLabel = styled.span`
  font-size: 14px;
`;
